import React, { useCallback } from 'react';

import { BrandingValue, CoBrandingValue, ConfigResponse } from '../../store/modules/merchant/types';
import { useMerchant } from '../../contexts/MerchantContext';
import { useGetConfigsQuery, useUploadLogoMutation } from '../../store/modules/merchant/api';
import { BrandingForm } from './components/BrandingForm';
import { useConfigUpsert } from '../../hooks';
import type { ConfigToUpsert } from '../../hooks/useConfigUpsert';
import { toast } from '../../util/toast';
import Logger from '../../util/logger';
import PageWrapper from '../../common/PageWrapper';

export const Branding = () => {
  const { merchantId, isLoading: isLoadingMerchant, canUpdateMerchantSettings } = useMerchant();
  const { data: configs, isLoading: isLoadingConfigs } = useGetConfigsQuery(merchantId, {
    skip: !merchantId,
  });
  const [uploadLogo, { isLoading: isUploading }] = useUploadLogoMutation();
  const { onConfigUpsertBatch, isLoading: isSaving } = useConfigUpsert(merchantId);

  const isLoading = isLoadingMerchant || isLoadingConfigs;

  const brandingConfig = configs?.find((c) => c.name === 'MERCHANT_BRANDING') as
    ConfigResponse<BrandingValue> | undefined;

  const cobrandingConfig = configs?.find((c) => c.name === 'CO_BRANDING') as
    ConfigResponse<CoBrandingValue> | undefined;

  const hasModifyAccess = canUpdateMerchantSettings();

  const onSave = useCallback(
    async (
      value: BrandingValue,
      coBrandingValue?: CoBrandingValue,
      withFileUploadError?: boolean,
    ) => {
      try {
        const configsToSave: ConfigToUpsert[] = [
          {
            enabled: true,
            name: 'MERCHANT_BRANDING',
            value,
            configId: brandingConfig?.configId,
            merchantId: brandingConfig?.merchantId,
          },
        ];
        if (coBrandingValue) {
          configsToSave.push({
            enabled: true,
            name: 'CO_BRANDING',
            value: coBrandingValue,
            configId: cobrandingConfig?.configId,
            merchantId: cobrandingConfig?.merchantId,
          });
        }
        await onConfigUpsertBatch(configsToSave);
        toast.success(
          withFileUploadError
            ? 'Changes saved partially. Logo upload failed. Please try again later or contact Skipify Support'
            : 'Changes saved successfully',
        );
      } catch (e) {
        Logger.error(e);
        toast.error(
          'An error occurred while saving these changes. Please try again later or contact Skipify Support',
        );
      }
    },
    [
      onConfigUpsertBatch,
      brandingConfig?.configId,
      brandingConfig?.merchantId,
      cobrandingConfig?.configId,
      cobrandingConfig?.merchantId,
    ],
  );

  return (
    <PageWrapper
      showParentHierarchyBanner
      title="Branding"
      description="This page will allow you to configure the Skipify experience to show your company name, brand color,
        and logo."
      isLoading={isLoading}
    >
      <BrandingForm
        key={brandingConfig?.merchantId ?? merchantId}
        branding={brandingConfig?.value}
        cobranding={cobrandingConfig?.value}
        readOnly={!hasModifyAccess}
        uploadLogo={(file, type?) =>
          uploadLogo({ merchantId, file, type }).unwrap().catch(Logger.error)
        }
        isSaving={isSaving || isUploading}
        onSave={onSave}
      />
    </PageWrapper>
  );
};
